import { VESSEL_CARD_BALTIC_INDEX_OPTIONS } from "../constants/vessel-card-baltic.constants";
import { VesselCardBalticIndex, VesselCardBalticPanelProps } from "../models/vessel-card.models";
import { formatVesselCardBalticPercent, formatVesselCardMetric } from "../utils/vessel-card-baltic.utils";
import VesselCardPerformanceGraph from "./VesselCardPerformanceGraph";
import { twMerge } from "tailwind-merge";

const indicatorClassNames = {
  positive: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  neutral: "text-teal-700 dark:text-teal-300",
  negative: "text-red-600 dark:text-red-400",
};

// Displays expected vessel performance against the selected Baltic benchmark at the same speed.
// Identifies the benchmark ship version and keeps missing values unavailable rather than displaying zero consumption.
const VesselCardBalticPanel = ({
  rows,
  index,
  onIndexChange,
  benchmarkAvailable,
  fuelGrade,
  view = "table",
}: VesselCardBalticPanelProps) => {
  const selectedIndexOption = VESSEL_CARD_BALTIC_INDEX_OPTIONS.find((option) => option.value === index);
  const indexLabel = selectedIndexOption?.label ?? index;

  return (
    <div data-test="vessel-card-baltic-comparison">
      <div
        className="mb-3 flex flex-wrap items-end gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30"
        data-test="vessel-card-baltic-index-selector"
      >
        <label className="flex min-w-[160px] flex-col gap-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
          Baltic index
          <select
            value={index}
            onChange={(event) => onIndexChange(event.currentTarget.value as VesselCardBalticIndex)}
            aria-describedby="vessel-card-baltic-index-help vessel-card-baltic-index-version"
            data-test="vessel-card-baltic-index-select"
            className="rounded-md border border-green bg-green px-3 py-2 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
          >
            {VESSEL_CARD_BALTIC_INDEX_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <div className="max-w-md pb-1 text-xs text-gray-500 dark:text-gray-400">
          <p id="vessel-card-baltic-index-help">
            Select BCI 182, BCI 180, BPI 82, BSI 63, BSI 58 or BHSI 38. Benchmark speed, consumption and comparison
            percentages follow the selected index. Benchmark consumption excludes diesel/MGO.
          </p>
          <p
            id="vessel-card-baltic-index-version"
            className="mt-1 font-medium"
            data-test="vessel-card-baltic-index-version"
          >
            {selectedIndexOption?.description}
          </p>
        </div>
      </div>

      {!benchmarkAvailable && (
        <p
          role="status"
          className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
          data-test="vessel-card-baltic-benchmark-unavailable"
        >
          Benchmark data is unavailable for {indexLabel}. Expected vessel performance is shown; benchmark values and
          comparison percentages are unavailable.
        </p>
      )}

      {view === "graph" ? (
        <VesselCardPerformanceGraph
          rows={rows.map((row) => ({
            ...row,
            comparisonSpeed: row.indexSpeed,
            comparisonConsumption: row.indexConsumption,
          }))}
          comparison="baltic"
          sourceLabel={indexLabel}
          fuelGrade={fuelGrade}
        />
      ) : (
        <>
          <div className="overflow-x-auto" data-test="vessel-card-baltic-table-wrapper">
            <table
              className="w-full min-w-[740px] border-separate border-spacing-0 text-sm"
              data-test="vessel-card-baltic-table"
              aria-describedby="vessel-card-baltic-percentage-help"
            >
              <caption className="sr-only">
                Expected vessel performance compared with the {indexLabel} Baltic index ship
              </caption>
              <thead className="text-xs text-gray-500 dark:text-gray-400">
                <tr>
                  <th rowSpan={2} scope="col" className="pb-2 pr-3 text-left font-medium">
                    <span className="block">Mode</span>
                    <span
                      className="mt-1 block text-amber-600 dark:text-amber-400"
                      data-test="vessel-card-baltic-fuel-grade"
                      aria-live="polite"
                    >
                      Fuel grade: {fuelGrade ?? "Not available"}
                    </span>
                  </th>
                  <th
                    colSpan={2}
                    scope="colgroup"
                    title="Expected clean-hull speed and consumption for the selected vessel."
                    className="border-l border-gray-200 px-2 pb-1 text-center font-medium dark:border-gray-700"
                  >
                    Expected performance
                  </th>
                  <th
                    colSpan={2}
                    scope="colgroup"
                    className="border-l border-gray-200 px-2 pb-1 text-center font-medium dark:border-gray-700"
                  >
                    Baltic index ship ({indexLabel})
                  </th>
                  <th
                    colSpan={2}
                    scope="colgroup"
                    title="Expected consumption × (index speed ÷ expected speed)^2.8"
                    className="border-l border-gray-200 px-2 pb-1 text-center font-medium dark:border-gray-700"
                  >
                    Expected @ index speed
                  </th>
                  <th
                    rowSpan={2}
                    scope="col"
                    title="Index ship consumption ÷ expected consumption at index speed × 100. Above 100% means the expected vessel uses less fuel."
                    className="border-l border-gray-200 px-3 pb-2 text-center font-medium dark:border-gray-700"
                  >
                    Expected perf vs index ship cons %
                  </th>
                </tr>
                <tr>
                  {Array.from({ length: 3 }).flatMap((_, groupIndex) =>
                    ["Speed", "Cons"].map((label) => (
                      <th
                        key={`${groupIndex}-${label}`}
                        scope="col"
                        title={
                          label === "Speed"
                            ? "Speed in knots"
                            : groupIndex === 1
                              ? "Benchmark consumption in t/day; diesel/MGO excluded"
                              : "Consumption in t/day"
                        }
                        className={twMerge(
                          "px-2 pb-2 text-center font-medium",
                          label === "Speed" && "border-l border-gray-200 dark:border-gray-700",
                        )}
                      >
                        {label}
                      </th>
                    )),
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.mode}
                    data-test={`vessel-card-baltic-row-${row.mode.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    <th scope="row" className="whitespace-nowrap py-3 pr-3 text-left font-semibold">
                      {row.mode}
                    </th>
                    {[
                      row.expectedSpeed,
                      row.expectedConsumption,
                      row.indexSpeed,
                      row.indexConsumption,
                      row.expectedAtIndexSpeed,
                      row.expectedConsumptionAtIndexSpeed,
                    ].map((value, columnIndex) => (
                      <td
                        key={columnIndex}
                        className={twMerge(
                          "px-2 py-3 text-center",
                          columnIndex % 2 === 0 && "border-l border-gray-200 dark:border-gray-700",
                        )}
                      >
                        {formatVesselCardMetric(value)}
                      </td>
                    ))}
                    <td
                      className={twMerge(
                        "border-l border-gray-200 px-3 py-3 text-center text-xs font-semibold dark:border-gray-700",
                        indicatorClassNames[row.indicatorTone],
                      )}
                    >
                      {formatVesselCardBalticPercent(row.comparisonPercent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p
            id="vessel-card-baltic-percentage-help"
            className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400"
            data-test="vessel-card-baltic-percentage-help"
          >
            Above 100%: expected vessel uses less fuel than the index ship. Below 100%: uses more fuel. At 100%: uses
            the same fuel, at index speed.
          </p>
          <details className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-test="vessel-card-baltic-formulas">
            <summary
              className="cursor-pointer font-semibold text-teal-600 dark:text-teal-300"
              data-test="vessel-card-baltic-formulas-toggle"
            >
              Calculation details
            </summary>
            <p className="mt-2">Expected cons at index speed = Expected cons × (Index speed ÷ Expected speed)^2.8</p>
            <p className="mt-1">Comparison % = Index ship cons ÷ Expected cons at index speed × 100</p>
            <p className="mt-1">Baltic benchmark consumption is in t/day with diesel/MGO excluded.</p>
          </details>
        </>
      )}
    </div>
  );
};

export default VesselCardBalticPanel;
